import React from 'react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { FEATURES, Feature } from '../constants';


const AppUIMockup = () => (
  <div className="relative w-full max-w-4xl mx-auto mt-12">
    <Card className="p-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-dot-grid [mask-image:radial-gradient(ellipse_at_center,white_60%,transparent_100%)]"></div>
      
      {/* Desktop Layout - Hidden on mobile */}
      <div className="hidden md:block">
        {/* SVG Arrows Overlay */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <svg className="w-full h-full" preserveAspectRatio="xMidYMid meet" viewBox="0 0 520 250">
            <path
              d="M215 75 C 260 75, 260 55, 305 55"
              stroke="#94a3b8"
              strokeWidth="1.5"
              fill="none"
              strokeDasharray="4 4"
            />
            <path
              d="M215 75 C 250 75, 250 165, 305 165"
              stroke="#94a3b8"
              strokeWidth="1.5"
              fill="none"
              strokeDasharray="4 4"
            />
          </svg>
        </div>

        {/* Desktop Mock Cards */}
        <div className="relative z-10 flex justify-center items-start space-x-16">
          {/* Root Card */}
          <div className="bg-pink-100/80 border border-pink-200/50 rounded-lg p-4 shadow-md w-48 text-center flex-shrink-0">
            <div className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full inline-block mb-2">Root</div>
            <div className="font-bold text-slate-800 text-sm">Brainstorming Main I...</div>
            <div className="text-xs text-slate-500">Project Lead</div>
          </div>

          {/* Branch Cards */}
          <div className="flex flex-col space-y-8 flex-shrink-0">
            <div className="bg-blue-100/80 border border-blue-200/50 rounded-lg p-4 shadow-md w-48 text-center">
              <div className="bg-yellow-400 text-white text-xs font-bold px-2 py-0.5 rounded-full inline-block mb-2">Active</div>
              <div className="font-bold text-slate-800 text-sm">Marketing Strategy</div>
              <div className="text-xs text-slate-500">Marketing Expert</div>
            </div>
            <div className="bg-yellow-100/80 border border-yellow-200/50 rounded-lg p-4 shadow-md w-48 text-center">
              <div className="bg-yellow-400 text-white text-xs font-bold px-2 py-0.5 rounded-full inline-block mb-2">Active</div>
              <div className="font-bold text-slate-800 text-sm">Timeline & Logistics</div>
              <div className="text-xs text-slate-500">Operations Manager</div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout - Hidden on desktop */}
      <div className="md:hidden w-full max-w-sm mx-auto">
        {/* Root Node */}
        <div className="relative">
          <div className="bg-pink-100/80 border border-pink-200/50 rounded-lg p-4 shadow-md text-center">
            <div className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full inline-block mb-2">Root</div>
            <div className="font-bold text-slate-800 text-sm">Brainstorming Main I...</div>
            <div className="text-xs text-slate-500">Project Lead</div>
          </div>
          {/* Connecting line segment pointing down from the root node */}
          <div className="absolute top-full left-4 h-8 w-0.5 bg-slate-300" aria-hidden="true"></div>
        </div>

        {/* Children Section */}
        <div className="relative mt-8">
          {/* Vertical connecting line for children */}
          <div className="absolute top-0 left-0 h-full w-0.5 bg-slate-300 ml-4" aria-hidden="true"></div>
          
          <div className="flex flex-col gap-8 ml-12">
            {/* Marketing Strategy Card */}
            <div className="relative">
              {/* Horizontal line from vertical line to the card */}
              <div className="absolute top-1/2 -left-8 w-8 h-0.5 bg-slate-300" aria-hidden="true"></div>
              {/* Dot on the vertical line */}
              <div className="absolute top-1/2 -left-8 w-2 h-2 bg-slate-400 rounded-full transform -translate-x-1/2 -translate-y-1/2 border-2 border-slate-50" aria-hidden="true"></div>
              
              <div className="bg-blue-100/80 border border-blue-200/50 rounded-lg p-4 shadow-md text-center">
                <div className="bg-yellow-400 text-white text-xs font-bold px-2 py-0.5 rounded-full inline-block mb-2">Active</div>
                <div className="font-bold text-slate-800 text-sm">Marketing Strategy</div>
                <div className="text-xs text-slate-500">Marketing Expert</div>
              </div>
            </div>

            {/* Timeline & Logistics Card */}
            <div className="relative">
              {/* Horizontal line from vertical line to the card */}
              <div className="absolute top-1/2 -left-8 w-8 h-0.5 bg-slate-300" aria-hidden="true"></div>
              {/* Dot on the vertical line */}
              <div className="absolute top-1/2 -left-8 w-2 h-2 bg-slate-400 rounded-full transform -translate-x-1/2 -translate-y-1/2 border-2 border-slate-50" aria-hidden="true"></div>
              
              <div className="bg-yellow-100/80 border border-yellow-200/50 rounded-lg p-4 shadow-md text-center">
                <div className="bg-yellow-400 text-white text-xs font-bold px-2 py-0.5 rounded-full inline-block mb-2">Active</div>
                <div className="font-bold text-slate-800 text-sm">Timeline & Logistics</div>
                <div className="text-xs text-slate-500">Operations Manager</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  </div>
);


const HeroSection = () => (
  <section className="relative text-center py-20 md:py-24 overflow-hidden">
    <div className="container mx-auto px-6 relative z-10">
      <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-slate-900">
        From Scattered Thoughts to Structured Brilliance
      </h1>
      <p className="mt-6 text-lg md:text-xl max-w-3xl mx-auto text-slate-600">
        ThreadIt is your AI-powered canvas to capture, connect, and cultivate ideas like never before. Transform your brainstorming into actionable insights.
      </p>
      <div className="mt-10 flex justify-center space-x-4">
        <Button to="/app" className="px-8 py-3 text-lg">Get Started For Free</Button>
      </div>
      <AppUIMockup />
    </div>
  </section>
);

const FeaturesSection = () => (
  <section id="features" className="py-20">
    <div className="container mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900">The Future of Brainstorming is Here</h2>
        <p className="mt-4 text-lg text-slate-600">Everything you need to think clearly and creatively.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {FEATURES.map((feature: Feature) => (
          <Card key={feature.name} className="flex flex-col items-start hover:border-emerald-500/50 hover:scale-105 transition-all duration-300">
            <div className="bg-emerald-500/10 p-3 rounded-lg mb-4">
               {feature.icon}
            </div>
            <h3 className="text-xl font-semibold mb-2 text-slate-800">{feature.name}</h3>
            <p className="text-slate-600">{feature.description}</p>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

const HowItWorksSection = () => (
  <section className="py-20 bg-white/50">
    <div className="container mx-auto px-6">
        <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Unleash Your Mind in 3 Simple Steps</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center text-2xl font-bold text-emerald-500 mb-4">1</div>
                <h3 className="text-xl font-semibold mb-2 text-slate-800">Capture</h3>
                <p className="text-slate-600">Start with a core idea. Write down your initial thoughts on our infinite canvas.</p>
            </div>
            <div className="flex flex-col items-center">
                 <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center text-2xl font-bold text-emerald-500 mb-4">2</div>
                <h3 className="text-xl font-semibold mb-2 text-slate-800">Branch & Explore</h3>
                <p className="text-slate-600">Dive deeper into any concept. Create branches to explore tangents with full AI context control.</p>
            </div>
            <div className="flex flex-col items-center">
                 <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center text-2xl font-bold text-emerald-500 mb-4">3</div>
                <h3 className="text-xl font-semibold mb-2 text-slate-800">Synthesize</h3>
                <p className="text-slate-600">Let our AI merge, summarize, and find connections, turning your brainstorm into a structured plan.</p>
            </div>
        </div>
    </div>
  </section>
);

const CtaSection = () => (
    <section className="py-20">
        <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                Ready to Revolutionize Your Brainstorming?
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
                Stop juggling messy notes and start building your next big idea with clarity and focus.
            </p>
            <div className="mt-8">
                <Button to="/app" className="px-10 py-4 text-xl">Start Thinking Clearly Today</Button>
            </div>
        </div>
    </section>
);


const HomePage: React.FC = () => {
  return (
    <>
      <HeroSection />
      <FeaturesSection />
      <HowItWorksSection />
      <CtaSection />
    </>
  );
};

export default HomePage;